// TileTactician web solver — the in-browser app. Bundles the REAL engine
// (src/engine/*) so the solver here is byte-for-byte the same one shipping in
// the app; only the dictionary loader and the UI are web-specific.
import './styles.css';
import { solve } from '../../src/engine/solver';
import { createEmptyBoard, getBonusType, TILE_VALUES, type BoardType } from '../../src/engine/scoring';
import { BOARD_SIZE } from '../../src/engine/constants';
import type { Board, Placement } from '../../src/engine/types';
import type { Trie } from '../../src/engine/trie';
import { loadWebDictionary } from './web-dict';

// Public values (same ones the app ships in its bundle; the secrets stay server-side).
const SUPABASE_URL = 'https://dwoxmdphxvickfcwnmng.supabase.co';
const ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR3b3htZHBoeHZpY2tmY3dubW5nIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzMwNDY0OTQsImV4cCI6MjA4ODYyMjQ5NH0.B9ARMRdMWIji0lFhJ_Du0byoKgGzQiscEVAF3YXMlBs';
const PROXY = `${SUPABASE_URL}/functions/v1/ai-proxy`;
const OPENAI_MODEL = 'gpt-5.4-mini-2026-03-17';

// Verbatim from src/utils/ocr-llm-board.ts so the web read matches the certified path.
const BOARD_READ_PROMPT =
  'You are reading a Crossplay word game board screenshot.\n\n' +
  'TILE IDENTIFICATION:\n' +
  '- PLAYED TILES (dark blue, white letter, point number in top-right): Output the LETTER.\n' +
  '- EVERYTHING ELSE (empty, bonus squares, labels): Output a PERIOD (.)\n\n' +
  'Visual guide:\n' +
  '- Tiles: dark blue, contiguous (no white borders between adjacent tiles)\n' +
  '- Empty cells: beige, separated by white grid lines\n' +
  '- Bonus squares (2L, 3L, 2W, 3W): pastel colored, centered darker text\n' +
  '  → ALL of these are PERIOD (.) — do NOT write "2L" or "3W" etc.\n\n' +
  'OUTPUT: Exactly 16 lines, no other text.\n' +
  'Lines 1-15: 15 entries per line separated by spaces.\n' +
  '  ONLY two possible values per entry: a single UPPERCASE LETTER or a PERIOD.\n' +
  '  NEVER write bonus labels like 2L, 3L, 2W, 3W — use . instead.\n' +
  '  Example row: . . A . . B C D . . . . . . .\n' +
  'Line 16: Rack letters concatenated (e.g. "ABCDEFG") or "NONE".\n\n' +
  'CRITICAL: Every entry must be EXACTLY one character (letter or period).\n' +
  'Do NOT output 2L, 3L, 2W, 3W, DL, TL, DW, TW — use . for all of those.';

const SAMPLE: Array<[number, number, string]> = [
  [7, 4, 'T'], [7, 5, 'A'], [7, 6, 'C'], [7, 7, 'T'], [7, 8, 'I'], [7, 9, 'C'], [7, 10, 'S'],
];
const SAMPLE_RACK = 'AEIOULS';

const $ = <T extends HTMLElement>(s: string) => document.querySelector(s) as T | null;
const bonusLabel = (b: string) => (b === 'TW' ? '3W' : b === 'DW' ? '2W' : b === 'TL' ? '3L' : b === 'DL' ? '2L' : '');

let trie: Trie | null = null;
let boardType: BoardType = 'crossplay';
const letters: (string | null)[] = new Array(BOARD_SIZE * BOARD_SIZE).fill(null);
const rack: string[] = new Array(7).fill('');
let selCell: number | null = null;
let selRack: number | null = null;
let solveCtrl: AbortController | null = null;
let highlighted: number[] = [];
let readVersion = 0;   // cancels in-flight / superseded screenshot reads
let dictFailed = false;

const boardEl = $('#board')!;
const rackEl = $('#rack')!;
const solveBtn = $('#solve') as HTMLButtonElement;
const solveStatus = $('#solveStatus')!;
const solveTime = $('#solveTime')!;
const resultsEl = $('#results')!;
const resultsCount = $('#resultsCount')!;
const dropEl = $('#drop')!;
const dropImg = $('#dropImg') as HTMLImageElement;
const dictStatus = $('#dictStatus')!;
const tokenInput = $('#sessionToken') as HTMLInputElement;

const cellEls: HTMLDivElement[] = [];
const rackEls: HTMLDivElement[] = [];

// ── build the static DOM ──────────────────────────────────────────────────
for (let i = 0; i < BOARD_SIZE * BOARD_SIZE; i++) {
  const cell = document.createElement('div');
  cell.className = 'cell';
  cell.setAttribute('role', 'gridcell');
  cell.dataset.i = String(i);
  const bl = document.createElement('span'); bl.className = 'bl';
  const lt = document.createElement('span'); lt.className = 'lt';
  const pv = document.createElement('span'); pv.className = 'pv';
  cell.append(bl, lt, pv);
  cell.addEventListener('click', () => selectCell(i));
  boardEl.appendChild(cell);
  cellEls.push(cell);
}
for (let j = 0; j < 7; j++) {
  const slot = document.createElement('div');
  slot.className = 'rslot';
  slot.dataset.j = String(j);
  const slt = document.createElement('span'); slt.className = 'lt';
  const spv = document.createElement('span'); spv.className = 'pv';
  slot.append(slt, spv);
  slot.addEventListener('click', () => selectRack(j));
  rackEl.appendChild(slot);
  rackEls.push(slot);
}

// ── painting ──────────────────────────────────────────────────────────────
function paintCell(i: number) {
  const el = cellEls[i];
  const r = (i / BOARD_SIZE) | 0;
  const c = i % BOARD_SIZE;
  const b = getBonusType(r, c, boardType);
  const L = letters[i];
  el.className = 'cell' + (b !== 'normal' ? ' b-' + b : '') + (L ? ' filled' : '') + (i === selCell ? ' sel' : '');
  el.querySelector('.bl')!.textContent = bonusLabel(b);
  el.querySelector('.lt')!.textContent = L ?? '';
  el.querySelector('.pv')!.textContent = L ? String(TILE_VALUES[L] ?? '') : '';
}
function paintRack(j: number) {
  const el = rackEls[j];
  const v = rack[j];
  el.className = 'rslot' + (v ? ' filled' : '') + (v === ' ' ? ' blank' : '') + (j === selRack ? ' sel' : '');
  el.querySelector('.lt')!.textContent = v && v !== ' ' ? v : '';
  el.querySelector('.pv')!.textContent = v && v !== ' ' ? String(TILE_VALUES[v] ?? '') : '';
}
const paintAll = () => { for (let i = 0; i < cellEls.length; i++) paintCell(i); for (let j = 0; j < 7; j++) paintRack(j); };

function selectCell(i: number) { selCell = selCell === i ? null : i; selRack = null; paintAll(); }
function selectRack(j: number) { selRack = selRack === j ? null : j; selCell = null; paintAll(); }

// ── keyboard ──────────────────────────────────────────────────────────────
window.addEventListener('keydown', (e) => {
  const ae = document.activeElement as HTMLElement | null;
  const tag = ae?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') return;
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'v') return; // let paste fire
  // The dropzone is a focusable div[role=button]; give it real button semantics so
  // Space/Enter activate the picker instead of leaking into the board/rack handler
  // (Space used to silently drop a blank tile into the rack).
  if (ae === dropEl) {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); dropEl.click(); }
    return;
  }
  if (e.key === 'Enter') { if (tag !== 'BUTTON') { e.preventDefault(); doSolve(); } return; }
  if (tag === 'BUTTON') return;

  if (e.key.startsWith('Arrow') && selCell != null) {
    e.preventDefault();
    const r = (selCell / BOARD_SIZE) | 0, c = selCell % BOARD_SIZE;
    if (e.key === 'ArrowRight' && c < 14) selCell++;
    else if (e.key === 'ArrowLeft' && c > 0) selCell--;
    else if (e.key === 'ArrowDown' && r < 14) selCell += BOARD_SIZE;
    else if (e.key === 'ArrowUp' && r > 0) selCell -= BOARD_SIZE;
    paintAll();
    return;
  }

  const letter = e.key.length === 1 && /^[a-zA-Z]$/.test(e.key) ? e.key.toUpperCase() : null;
  const isBack = e.key === 'Backspace' || e.key === 'Delete';
  const isSpace = e.key === ' ';
  if (!letter && !isBack && !isSpace) return;

  if (selCell != null) {
    e.preventDefault();
    if (letter) {
      const typed = selCell;
      letters[typed] = letter;
      if (typed % BOARD_SIZE < 14) selCell = typed + 1; // advance selection first
      paintCell(typed);   // repaint the typed cell so the letter shows (sel ring now cleared)
      paintCell(selCell); // draw the selection ring on the next cell
    } else if (isBack) {
      letters[selCell] = null;
      paintCell(selCell);
    }
  } else {
    let j = selRack;
    if (j == null) {
      const empty = rack.indexOf('');
      if (empty === -1) return; // rack full with no selection: nowhere to put it, ignore gracefully
      j = empty;
    }
    selRack = j;
    e.preventDefault();
    if (letter) { rack[j] = letter; if (j < 6) selRack = j + 1; }
    else if (isSpace) { rack[j] = ' '; if (j < 6) selRack = j + 1; }
    else if (isBack) rack[j] = '';
    paintRack(j);
    if (selRack != null) paintRack(selRack);
  }
});

// ── paste / drop ──────────────────────────────────────────────────────────
const fileInput = document.createElement('input');
fileInput.type = 'file';
fileInput.accept = 'image/*';
fileInput.style.display = 'none';
document.body.appendChild(fileInput);
fileInput.addEventListener('change', () => { if (fileInput.files?.[0]) handleImage(fileInput.files[0]); });

dropEl.addEventListener('click', () => {
  if (dropEl.classList.contains('has-image')) clearImage();
  else fileInput.click();
});
dropEl.addEventListener('dragover', (e) => { e.preventDefault(); dropEl.classList.add('dragover'); });
dropEl.addEventListener('dragleave', () => dropEl.classList.remove('dragover'));
dropEl.addEventListener('drop', (e) => {
  e.preventDefault();
  dropEl.classList.remove('dragover');
  const f = (e as DragEvent).dataTransfer?.files?.[0];
  if (f && f.type.startsWith('image/')) handleImage(f);
});
window.addEventListener('paste', (e) => {
  for (const item of Array.from(e.clipboardData?.items ?? [])) {
    if (item.type.startsWith('image/')) { const f = item.getAsFile(); if (f) handleImage(f); return; }
  }
});

function clearImage() {
  readVersion++;                            // cancel any in-flight AI read for this image
  if (dropImg.src) URL.revokeObjectURL(dropImg.src);
  dropEl.classList.remove('has-image', 'reading');
  dropImg.hidden = true;
  dropImg.src = '';
}

async function handleImage(blob: Blob) {
  const myVersion = ++readVersion;          // a newer paste/clear will supersede this read
  if (dropImg.src) URL.revokeObjectURL(dropImg.src);
  const url = URL.createObjectURL(blob);
  dropImg.src = url;
  dropImg.hidden = false;
  dropEl.classList.add('has-image');

  const token = tokenInput.value.trim();
  if (!token) {
    dropEl.classList.remove('reading');   // not reading — clear any spinner left by a superseded read
    setStatus('Screenshot loaded — enter the board by hand, or paste a session token to auto-read it.');
    return;
  }
  dropEl.classList.add('reading');
  try {
    const b64 = await imageToBase64(blob);
    if (myVersion !== readVersion) return;
    const { grid, rack: readRack } = await aiReadBoard(b64, token);
    if (myVersion !== readVersion) return;
    fillBoard(grid, true);
    for (let j = 0; j < 7; j++) rack[j] = readRack[j] ?? '';
    paintAll();
    clearImage();
    doSolve();
  } catch (err) {
    if (myVersion !== readVersion) return;
    dropEl.classList.remove('reading');
    setStatus('AI read failed: ' + (err as Error).message + ' — enter the board by hand.');
  }
}

function imageToBase64(blob: Blob, maxSide = 1536): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      try {
        let { width, height } = img;
        const scale = Math.min(1, maxSide / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const cv = document.createElement('canvas');
        cv.width = width; cv.height = height;
        const ctx = cv.getContext('2d');
        if (!ctx) { reject(new Error('canvas unavailable')); return; }
        ctx.drawImage(img, 0, 0, width, height);
        resolve(cv.toDataURL('image/jpeg', 0.85).split(',')[1]);
      } catch (e) {
        reject(e instanceof Error ? e : new Error('canvas conversion failed'));
      }
    };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('could not decode image')); };
    img.src = objectUrl;
  });
}

async function aiReadBoard(b64: string, token: string) {
  const res = await fetch(`${PROXY}?service=openai`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, apikey: ANON_KEY },
    body: JSON.stringify({
      model: OPENAI_MODEL,
      max_completion_tokens: 1200,
      messages: [{ role: 'user', content: [
        { type: 'text', text: BOARD_READ_PROMPT },
        { type: 'image_url', image_url: { url: `data:image/jpeg;base64,${b64}` } },
      ] }],
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = (await res.json()) as { choices?: Array<{ message?: { content?: string } }> };
  const text = json.choices?.[0]?.message?.content ?? '';
  return parseBoardRead(text);
}

function parseBoardRead(content: string): { grid: (string | null)[][]; rack: string } {
  let clean = content.trim();
  if (clean.startsWith('```')) clean = clean.replace(/^```[a-zA-Z]*\n/, '').replace(/\n```$/, '');
  const lines = clean.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
  const re = /\b(2L|3L|2W|3W|DL|TL|DW|TW)\b/gi;
  // A row is "shaped" iff, after normalising bonus labels, it is 13-15 single-char tokens
  // (letters or '.'). Prose / wrapped lines contain multi-char words and fail this, which
  // lets us locate the real 15-row grid even when the model prefaced chatter.
  const shaped = (raw: string): boolean => {
    const norm = raw.replace(re, '.');
    const toks = norm.includes(' ') ? norm.split(/\s+/).filter(Boolean) : [...norm];
    return toks.length >= 13 && toks.length <= 15 && toks.every((t) => t.length === 1 && /[A-Z.]/.test(t));
  };
  // Skip any leading preamble; the grid is the first run of 15 consecutive shaped rows.
  // A prose prefix shifts this window past itself (realigning correctly); prose interleaved
  // inside the grid breaks the consecutive run and is rejected instead of corrupting the board.
  let start = 0;
  while (start < lines.length && !shaped(lines[start])) start++;
  if (start + BOARD_SIZE > lines.length) throw new Error('no 15-row grid found');
  for (let r = 0; r < BOARD_SIZE; r++) {
    if (!shaped(lines[start + r])) throw new Error('grid row malformed or interleaved prose');
  }
  const grid: (string | null)[][] = [];
  let letterCount = 0;
  for (let r = 0; r < BOARD_SIZE; r++) {
    const line = lines[start + r].replace(re, '.');
    const toks = line.includes(' ') ? line.split(/\s+/) : [...line];
    const row: (string | null)[] = toks.map((t) => (/^[A-Za-z]$/.test(t) ? t.toUpperCase() : null));
    while (row.length < BOARD_SIZE) row.push(null);
    const sliced = row.slice(0, BOARD_SIZE);
    for (const cell of sliced) if (cell) letterCount++;
    grid.push(sliced);
  }
  let rackStr = '';
  const rackIdx = start + BOARD_SIZE;
  if (rackIdx < lines.length) {
    const rl = lines[rackIdx].replace(/[^A-Za-z]/g, '').toUpperCase();
    if (rl !== 'NONE') rackStr = rl.slice(0, 7);
  }
  if (letterCount === 0 && !rackStr) throw new Error('no tiles read from the screenshot');
  return { grid, rack: rackStr };
}

function fillBoard(grid: (string | null)[][], animate: boolean) {
  for (let i = 0; i < letters.length; i++) letters[i] = grid[(i / BOARD_SIZE) | 0][i % BOARD_SIZE];
  paintAll();
  if (animate) {
    letters.forEach((L, i) => {
      if (!L) return;
      const el = cellEls[i];
      el.style.animationDelay = (i % BOARD_SIZE + ((i / BOARD_SIZE) | 0)) * 12 + 'ms';
      el.classList.add('drop-anim');
    });
  }
}

// ── solve + render ────────────────────────────────────────────────────────
function setStatus(t: string) { solveStatus.textContent = t; }

async function doSolve() {
  if (!trie) { setStatus(dictFailed ? 'dictionary failed to load — cannot solve' : 'dictionary still loading…'); return; }
  const board = createEmptyBoard(boardType);
  let hasLetter = false;
  for (let i = 0; i < letters.length; i++) {
    if (letters[i]) { board[(i / BOARD_SIZE) | 0][i % BOARD_SIZE].letter = letters[i] as Board[0][0]['letter']; hasLetter = true; }
  }
  const rackStr = rack.join('');
  if (!hasLetter && !rackStr) { setStatus('enter a board and a rack, then solve.'); return; }

  solveCtrl?.abort();
  const ctrl = new AbortController();
  solveCtrl = ctrl;
  solveBtn.classList.add('is-solving');
  solveBtn.disabled = true;
  setStatus('solving…');
  clearHighlight();

  const t0 = performance.now();
  try {
    const placements = await solve(board, rackStr, trie, ctrl.signal, boardType);
    if (ctrl !== solveCtrl) return;          // a newer solve started; drop these stale results
    const dt = Math.round(performance.now() - t0);
    renderResults(placements, dt);
    setStatus('');
  } catch (err) {
    if (ctrl !== solveCtrl) return;
    if (!(err as Error)?.message?.includes('abort')) setStatus('solve error: ' + (err as Error).message);
  } finally {
    if (ctrl === solveCtrl) {                // only the current solve owns the button state
      solveBtn.classList.remove('is-solving');
      solveBtn.disabled = false;
    }
  }
}

function coord(p: Placement) {
  return String.fromCharCode(65 + p.col) + (p.row + 1);
}
function cellsOf(p: Placement): number[] {
  const out: number[] = [];
  for (let k = 0; k < p.word.length; k++) {
    const r = p.direction === 'across' ? p.row : p.row + k;
    const c = p.direction === 'across' ? p.col + k : p.col;
    out.push(r * BOARD_SIZE + c);
  }
  return out;
}

function renderResults(placements: Placement[], dt: number) {
  solveTime.textContent = dt + ' ms';
  resultsEl.innerHTML = '';
  if (placements.length === 0) {
    resultsCount.textContent = 'no legal plays';
    const li = document.createElement('li');
    li.className = 'results-empty';
    li.textContent = 'No legal placements from this rack on this board. Try editing a square or the rack.';
    resultsEl.appendChild(li);
    return;
  }
  resultsCount.textContent = placements.length + (placements.length === 1 ? ' play' : ' plays');
  placements.forEach((p, idx) => {
    const li = document.createElement('li');
    li.className = 'res' + (p.scoreBreakdown.bingo ? ' bingo' : '');
    li.style.animationDelay = Math.min(idx, 40) * 22 + 'ms';

    const rk = document.createElement('span');
    rk.className = 'rk';
    rk.textContent = String(idx + 1);

    const mid = document.createElement('span');
    mid.className = 'mid';
    const word = document.createElement('span');
    word.className = 'word';
    word.textContent = p.word;
    mid.appendChild(word);
    if (p.scoreBreakdown.bingo) {
      const tag = document.createElement('span');
      tag.className = 'bingo-tag';
      tag.textContent = 'BINGO';
      mid.appendChild(tag);
    }
    const meta = document.createElement('span');
    meta.className = 'meta';
    meta.textContent = `${coord(p)} ${p.direction} · `;
    const used = document.createElement('span');
    used.className = 'used';
    used.textContent = p.tilesUsed.map((t) => (t === ' ' ? '?' : t)).join(' ');
    meta.appendChild(used);
    mid.appendChild(meta);

    const sc = document.createElement('span');
    sc.className = 'sc';
    sc.textContent = String(p.score);

    li.append(rk, mid, sc);
    const cells = cellsOf(p);
    li.addEventListener('mouseenter', () => highlight(cells));
    li.addEventListener('mouseleave', clearHighlight);
    resultsEl.appendChild(li);
  });
}

function highlight(cells: number[]) {
  clearHighlight();
  cells.forEach((i) => cellEls[i]?.classList.add('hl'));
  highlighted = cells;
}
function clearHighlight() {
  highlighted.forEach((i) => cellEls[i]?.classList.remove('hl'));
  highlighted = [];
}

// ── controls ──────────────────────────────────────────────────────────────
solveBtn.addEventListener('click', doSolve);
$('#clear')!.addEventListener('click', () => {
  letters.fill(null); rack.fill(''); selCell = null; selRack = null;
  paintAll(); clearHighlight(); resultsEl.innerHTML = ''; resultsCount.textContent = 'no plays yet'; solveTime.textContent = ''; setStatus('');
});
$('#sample')!.addEventListener('click', loadSample);
Array.from(document.querySelectorAll('.seg-btn')).forEach((b) => {
  b.addEventListener('click', () => {
    boardType = (b as HTMLElement).dataset.type as BoardType;
    document.querySelectorAll('.seg-btn').forEach((x) => {
      const on = x === b;
      x.classList.toggle('is-active', on);
      x.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    paintAll();
    resultsEl.innerHTML = ''; resultsCount.textContent = 'no plays yet'; solveTime.textContent = '';
    setStatus('board changed — press Solve.');
  });
});

function loadSample() {
  letters.fill(null);
  for (const [r, c, L] of SAMPLE) letters[r * BOARD_SIZE + c] = L;
  for (let j = 0; j < 7; j++) rack[j] = SAMPLE_RACK[j] ?? '';
  selCell = null; selRack = null;
  fillBoard(
    Array.from({ length: BOARD_SIZE }, (_, r) => Array.from({ length: BOARD_SIZE }, (_, c) => letters[r * BOARD_SIZE + c])),
    true,
  );
  for (let j = 0; j < 7; j++) paintRack(j);
}

// ── ambient: pointer parallax + glow ──────────────────────────────────────
let raf = 0;
window.addEventListener('pointermove', (e) => {
  if (raf) return;
  raf = requestAnimationFrame(() => {
    raf = 0;
    const dx = (e.clientX / window.innerWidth - 0.5) * -16;
    const dy = (e.clientY / window.innerHeight - 0.5) * -16;
    const root = document.documentElement.style;
    root.setProperty('--px', dx + 'px');
    root.setProperty('--py', dy + 'px');
    root.setProperty('--gx', e.clientX + 'px');
    root.setProperty('--gy', e.clientY + 'px');
  });
});

// ── scroll reveal for the loop ────────────────────────────────────────────
document.querySelectorAll('.step').forEach((s) => (s as HTMLElement).style.setProperty('--i', (s as HTMLElement).dataset.i ?? '0'));
const io = new IntersectionObserver((entries) => {
  entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('in'); io.unobserve(en.target); } });
}, { threshold: 0.3 });
document.querySelectorAll('.step').forEach((s) => io.observe(s));
// Fail-open: if the observer never fires for a step, never leave content hidden.
setTimeout(() => document.querySelectorAll('.step:not(.in)').forEach((s) => s.classList.add('in')), 1600);

// ── boot ─────────────────────────────────────────────────────────────────
loadSample();
loadWebDictionary()
  .then((t) => {
    trie = t;
    dictStatus.dataset.state = 'ready';
    dictStatus.textContent = `${t.getWordCount().toLocaleString()} words · ready`;
    if (letters.some((L) => L)) doSolve();
  })
  .catch((err) => {
    dictFailed = true;
    dictStatus.dataset.state = 'error';
    dictStatus.textContent = 'dictionary failed to load';
    console.error(err);
  });
